import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import ProCheckbox from "../../../Components/Inputs/CheckboxField";
import ProTextInput from "../../../Components/Inputs/TextField";
import ProSelectField from "../../../Components/Inputs/SelectField";
import ProToggleField from "../../../Components/Inputs/ToggleButton";
import ProButton from "../../../Components/ProButton";
import { Grid, Divider, Typography, Link, Tooltip, IconButton, Box } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InputWrapper from "../../../Components/Inputs/InputWrapper";
import { payTypePositionOption } from "../../../../utils/dropdownHelper";
import FreeSoloAutoComplete from "../../../Components/Inputs/FreeSoloAutoComplete";
import ProCurrencyField from "../../../Components/Inputs/CurrencyField";
import { fieldTruePermission } from "../../../../utils/formHelper";
import DateField from "../../../Components/Inputs/DateField";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { warningToast } from "../../../../utils/toastHelper";
import dayjs from "dayjs";
import ApprenticeTable from "../../../Components/ApprenticeTable/ApprenticeTable";
import useColumn from "../../../../utils/customHooks/useColumns";
import { ProjectJobsUnionFringeForm } from "./ProjectJobsUnionFringeForm";
import ProjectJobsOvertimeUnionFringeForm from "./ProjectJobsOvertimeUnionFringeForm";
import { GET_ALL_JOB_TITLE } from "../../../../utils/services/apiPath";
import InfiniteScrollSelect from "../../../Components/Inputs/InfiniteScrollSelect";
import { postApi } from "../../../../utils/services";
import { paginatedDropdownConfig } from "../../../../utils/dropdownHelper";
import { useTheme } from "@mui/material/styles";
import DialogModal from "../../../Components/DialogModel";
import ProRadioGroup from "../../../Components/Inputs/RadioGroup";
import CustomCurrencyField from "../../../Components/Inputs/CustomCurrencyField";
import FormSectionSubHeader from "../../../Components/FormSectionHeader/FormSectionSubHeader";

import overtimeWageIcon from "../../../Assets/Icons/overtime-wage-icon.png";
import doubleTimeWageIcon from "../../../Assets/Icons/doubleTime-wage-icon.png";

export const checkDateWarning = (date, label) => {
  if (!date) return;
  const selectedDate = dayjs(date);
  const today = dayjs();
  const fiveYearsLater = today.add(5, "year");
  const tenYearsLater = today.add(10, "year");

  if (selectedDate.isBefore(today, "day")) {
    warningToast(`The selected ${label} date is in the past.`);
  } else if (selectedDate.isAfter(fiveYearsLater) && selectedDate.isBefore(tenYearsLater)) {
    warningToast(`The selected ${label} date is between 5 and 10 years in the future.`);
  }
};

const containerSx = {
  px: { xs: 2, sm: 2, md: 2 }
};

const cardSx = {
  border: "1px solid #E0E0E0",
  borderRadius: "8px",
  p: 2,
  mt: 1,
  width: "100%"
};

const radioGroupSx = {
  width: "100%",

  "& .MuiRadioGroup-root": {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "1fr",
      md: "1fr 1fr"
    },
    columnGap: "24px",
    rowGap: "16px"
  },

  "& .MuiFormControlLabel-root": {
    margin: 0,
    alignItems: "flex-start",
    padding: "5px 0",
    width: {
      xs: "100%",
      sm: "100%",
      md: "50%"
    },
    display: "flex"
  },

  "& .MuiRadio-root": {
    marginLeft: "-6px"
  },

  "& .MuiFormControlLabel-root .MuiRadio-root": {
    alignSelf: "flex-start",
    padding: "0 9px 9px 9px"
  },

  "& .MuiTypography-root": {
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: 1.4,
    color: "#161D2C",
    pb: "4px"
  },

  "& .MuiTypography-caption": {
    fontSize: "14px",
    color: "#3C4655",
    fontWeight: 400
  }
};

const laborOptions = [
  {
    id: "craft",
    label: "Craft / Trade Labor / Working Supervisor Position",
    description: "Hands-on, skilled trade, or field-based supervisory role"
  },
  {
    id: "admin",
    label: "Administrative / Support / Leadership role",
    description: "Office-based, support function, or management role"
  }
];

const ProjectJobsForm = ({
  formConfig,
  defaultFormProps,
  jobTitle,
  addJobPermisison,
  assignRAPInfoGuids,
  assignRAPWageScaleGuids,
  setValue,
  getValues,
  watch,
  trigger,
  jobSearchOptions,
  handleOpen,
  isEditModeOn,
  getPWCFilePath,
  getJobSearchOption,
  isFetchLabels,
  isNoRecordOnClassification,
  clrInfoMessage,
  setFinalApprenticeData,
  isOTWageOverridden,
  setIsOTWageOverridden,
  isOTFringeOverridden,
  setIsOTFringeOverridden,
  isDTWageOverridden,
  setIsDTWageOverridden,
  isDTFringeOverridden,
  setIsDTFringeOverridden,
  isUnionOTWageOverridden,
  setIsUnionOTWageOverridden,
  isUnionDTWageOverridden,
  setIsUnionDTWageOverridden,
  overrideErrors = { ot: false, dt: false }
}) => {
  const isUnionLaborFeatureEnabled = useFeatureFlagEnabled("is_union_labor_fields_enabled");
  const isUnionOvertimeDoubletimeEnabled = useFeatureFlagEnabled("is_union_overtime_doubletime_enabled");

  const isCraftTrade = watch("IsCraftTradeLaborPosition");
  const isExternalJobTitle = watch("isExternalJobTitle");
  const payTypePosition = watch("payTypePosition");
  const isApprenticeable = watch("isApprenticeable");
  const externalJobTitle = watch("externalJobTitle");
  const jobTitleId = watch("jobTitleId");
  const footnoteSickDays = watch("footnoteSickDays");
  const footnoteVacationDays = watch("footnoteVacationDays");
  const footnoteHolidays = watch("footnoteHolidays");
  const footnoteOthers = watch("footnoteOthers");
  const SAMFilename = watch("samFileName");
  const SAMFilenamePath = watch("samFilePath");
  const isAdministrative = watch("IsRole");
  const typeOfJob = watch("typeOfJob");
  const groupClassification = watch("groupClassification");
  const equipmentUsed = watch("equipmentUsed");
  const isCustomApprenticeOnboardingRequired = watch("isCustomApprenticeOnboardingRequired");
  const isUnion = watch("isUnion");
  const hasOTFringeOverride = watch("hasOTFringeOverride");
  const hasDTFringeOverride = watch("hasDTFringeOverride");
  const prevailingStraightTimeWage = watch("prevailingStraightTimeWage");
  const prevailingStraightTimeFringe = watch("prevailingStraightTimeFringe");
  const unionStraightTimeWage = watch("unionStraightTimeWage");
  const prevailingOverTimeWage = watch("prevailingOverTimeWage");
  const prevailingDoubleTimeWage = watch("prevailingDoubleTimeWage");
  const prevailingOverTimeFringeValue = watch("prevailingOverTimeFringe");
  const prevailingDoubleTimeFringeValue = watch("prevailingDoubleTimeFringe");
  const unionOverTimeWageValue = watch("unionOverTimeWage");
  const unionDoubleTimeWageValue = watch("unionDoubleTimeWage");

  const [isOTWageFocused, setIsOTWageFocused] = useState(false);
  const [isDTWageFocused, setIsDTWageFocused] = useState(false);
  const [isOTFringeFocused, setIsOTFringeFocused] = useState(false);
  const [isDTFringeFocused, setIsDTFringeFocused] = useState(false);
  const [isUnionOTWageFocused, setIsUnionOTWageFocused] = useState(false);
  const [isUnionDTWageFocused, setIsUnionDTWageFocused] = useState(false);

  const theme = useTheme();

  const otDtRequiredRules = (baseRules, label, { requirePositive = false } = {}) => {
    if (!isUnion) return baseRules;
    const rules = { ...(baseRules || {}), required: `${label} is required when Union Labor is enabled.` };
    if (requirePositive) {
      const baseValidate = baseRules?.validate;
      rules.validate = {
        ...(typeof baseValidate === "function" ? { _base: baseValidate } : baseValidate || {}),
        positive: value => {
          if (value === null || value === undefined || value === "") return true; // required handles empty
          return parseFloat(value) > 0 || `${label} must be greater than 0.`;
        }
      };
    }
    return rules;
  };

  const [jobTitleOptions, setJobTitleOptions] = useState([]);
  const [jobTitlePage, setJobTitlePage] = useState(1);
  const [jobTitleHasMore, setJobTitleHasMore] = useState(true);
  const [jobTitleLoading, setJobTitleLoading] = useState(false);
  const [invalidJobTitleModal, setInvalidJobTitleModal] = useState({ open: false, message: "" });
  const [localFormConfig, setLocalFormConfig] = useState(formConfig);

  const fetchJobTitles = async (pageIndex = 1, searchString = "") => {
    setJobTitleLoading(true);
    try {
      const payload = { ...paginatedDropdownConfig, pageIndex, searchString };

      const { data, error, hasNextPage } = await postApi(GET_ALL_JOB_TITLE, payload);
      if (error) {
        errorToast(error);
        return;
      }

      const newOptions = (data || []).map(item => ({
        label: item.jobTitleName,
        value: item.jobTitleId
      }));

      setJobTitleOptions(prev => {
        const combined = pageIndex === 1 ? newOptions : [...prev, ...newOptions];
        const currentValue = getValues("jobTitleId");
        if (currentValue && !combined.some(o => o.value === currentValue)) {
          const match = data?.find?.(d => d.jobTitleId === currentValue);
          if (match) {
            combined.push({
              label: match.jobTitleName,
              value: match.jobTitleId
            });
          }
        }

        return combined.filter((item, index, self) => index === self.findIndex(t => t.value === item.value));
      });

      setJobTitleHasMore(hasNextPage);
      setJobTitlePage(pageIndex);
    } finally {
      setJobTitleLoading(false);
    }
  };

  useEffect(() => {
    fetchJobTitles(1);
  }, []);

  useEffect(() => {
    const updatedConfig = { ...formConfig };
    if (isApprenticeable) {
      updatedConfig.assignRAPInfoGuid = {
        ...updatedConfig.assignRAPInfoGuid,
        rules: { required: "RAP is required when Apprenticeship Eligible!" }
      };
    } else {
      delete updatedConfig.assignRAPInfoGuid.rules;
    }
    setLocalFormConfig(updatedConfig);
    trigger?.("assignRAPInfoGuid");
  }, [isApprenticeable, formConfig, trigger]);

  const [apprenticeRows, setApprenticeRows] = useState([
    { label: "Apprentice 1", value: "" },
    { label: "Apprentice 2", value: "" }
  ]);
  const [additionalApprenticeValue, setAdditionalApprenticeValue] = useState("");

  const getJobTitleLabel = jobTitleId => {
    const jobTitleObj = jobTitle.find(item => item.value === jobTitleId);
    return jobTitleObj?.label;
  };

  const getApprenticeTitle = () => {
    if (isExternalJobTitle && externalJobTitle) return `${externalJobTitle} Apprentice`;
    else if (jobTitleId) return `${getJobTitleLabel(jobTitleId)} Apprentice`;
    return "";
  };

  const getDisabledDate = date => {
    return date.isAfter(dayjs().add(10, "year"));
  };

  useEffect(() => {
    const onboardingData = defaultFormProps?.formValues?.customApprenticeOnboardings || [];
    if (onboardingData.length === 0) {
      setApprenticeRows([
        { label: "Apprentice 1", value: "" },
        { label: "Apprentice 2", value: "" }
      ]);
      setAdditionalApprenticeValue("");
      setFinalApprenticeData([]);
      return;
    }

    const baseApprentices = onboardingData.filter(item => !item.isEachAdditionalApprentice).sort((a, b) => a.apprenticeNumber - b.apprenticeNumber);

    const baseRows = baseApprentices.map((item, index) => ({
      label: `Apprentice ${index + 1}`,
      value: item.journeyManRatio?.toString() || "",
      apprenticeNumber: index + 1
    }));

    const additional = onboardingData.find(item => item.isEachAdditionalApprentice);

    setApprenticeRows(baseRows);
    setAdditionalApprenticeValue(additional?.journeyManRatio?.toString() || "");
  }, [defaultFormProps?.formValues?.customApprenticeOnboardings]);

  useEffect(() => {
    const baseRows = apprenticeRows
      .filter(row => row.value.trim() !== "")
      .map((row, index) => ({
        apprenticeNumber: index + 1,
        journeyManRatio: parseInt(row.value),
        isEachAdditionalApprentice: false
      }));

    const additionalRow = additionalApprenticeValue.trim()
      ? [
          {
            apprenticeNumber: 0,
            journeyManRatio: parseInt(additionalApprenticeValue),
            isEachAdditionalApprentice: true
          }
        ]
      : [];

    const finalData = [...baseRows, ...additionalRow];

    setFinalApprenticeData(finalData);
    setValue("customApprenticeOnboardings", finalData);
  }, [apprenticeRows, additionalApprenticeValue, setValue]);

  useEffect(() => {
    if (!isCustomApprenticeOnboardingRequired) {
      // Clear rows, additional field, and synced form field
      setApprenticeRows([
        { label: "Apprentice 1", value: "" },
        { label: "Apprentice 2", value: "" }
      ]);
      setAdditionalApprenticeValue("");
      setFinalApprenticeData([]);
      setValue("customApprenticeOnboardings", []);
    }
  }, [isCustomApprenticeOnboardingRequired, setValue]);

  useEffect(() => {
    const value = watch("laborClassificationType");
    if (value === "craft") {
      setValue("IsCraftTradeLaborPosition", true);
      setValue("IsRole", false);
    } else if (value === "admin") {
      setValue("IsCraftTradeLaborPosition", false);
      setValue("IsRole", true);
    }
  }, [watch("laborClassificationType")]);

  useEffect(() => {
    const wage = parseFloat(prevailingStraightTimeWage);

    if (isNaN(wage)) {
      if (!isOTWageOverridden) {
        setValue("prevailingOverTimeWage", "");
      }

      if (!isDTWageOverridden) {
        setValue("prevailingDoubleTimeWage", "");
      }

      return;
    }

    if (!isOTWageOverridden) {
      setValue("prevailingOverTimeWage", (wage * 1.5).toFixed(2));
    }

    if (!isDTWageOverridden) {
      setValue("prevailingDoubleTimeWage", (wage * 2).toFixed(2));
    }

  }, [
    prevailingStraightTimeWage,
    isOTWageOverridden,
    isDTWageOverridden,
    setValue
  ]);

  useEffect(() => {

    if (!isOTFringeOverridden) {
      setValue("prevailingOverTimeFringe", prevailingStraightTimeFringe);
    }

    if (!isDTFringeOverridden) {
      setValue("prevailingDoubleTimeFringe", prevailingStraightTimeFringe);
    }

  }, [
    prevailingStraightTimeFringe,
    isOTFringeOverridden,
    isDTFringeOverridden,
    setValue
  ]);

  useEffect(() => {
    const wage = parseFloat(unionStraightTimeWage);

    if (isNaN(wage)) {
      if (!isUnionOTWageOverridden) {
        setValue("unionOverTimeWage", "");
      }

      if (!isUnionDTWageOverridden) {
        setValue("unionDoubleTimeWage", "");
      }

      return;
    }

    if (!isUnionOTWageOverridden) {
      setValue("unionOverTimeWage", (wage * 1.5).toFixed(2));
    }

    if (!isUnionDTWageOverridden) {
      setValue("unionDoubleTimeWage", (wage * 2).toFixed(2));
    }

  }, [
    unionStraightTimeWage,
    isUnionOTWageOverridden,
    isUnionDTWageOverridden,
    setValue
  ]);

  useEffect(() => {
    if (!isOTWageFocused && !prevailingOverTimeWage) {
      setIsOTWageOverridden(false);

      const wage = parseFloat(prevailingStraightTimeWage);
      if (!isNaN(wage)) {
        setValue("prevailingOverTimeWage", (wage * 1.5).toFixed(2));
      }
    }
  }, [isOTWageFocused, prevailingOverTimeWage, prevailingStraightTimeWage, setValue]);

  useEffect(() => {
    if (!isDTWageFocused && !prevailingDoubleTimeWage) {
      setIsDTWageOverridden(false);

      const wage = parseFloat(prevailingStraightTimeWage);
      if (!isNaN(wage)) {
        setValue("prevailingDoubleTimeWage", (wage * 2).toFixed(2));
      }
    }
  }, [isDTWageFocused, prevailingDoubleTimeWage, prevailingStraightTimeWage, setValue]);

  useEffect(() => {
    if (!isOTFringeFocused && !prevailingOverTimeFringeValue) {
      setIsOTFringeOverridden(false);

      if (prevailingStraightTimeFringe) {
        setValue("prevailingOverTimeFringe", prevailingStraightTimeFringe);
      }
    }
  }, [isOTFringeFocused, prevailingOverTimeFringeValue, prevailingStraightTimeFringe, setValue]);

  useEffect(() => {
    if (!isDTFringeFocused && !prevailingDoubleTimeFringeValue) {
      setIsDTFringeOverridden(false);

      if (prevailingStraightTimeFringe) {
        setValue("prevailingDoubleTimeFringe", prevailingStraightTimeFringe);
      }
    }
  }, [isDTFringeFocused, prevailingDoubleTimeFringeValue, prevailingStraightTimeFringe, setValue]);

  useEffect(() => {
    if (!isUnionOTWageFocused && !unionOverTimeWageValue) {
      setIsUnionOTWageOverridden(false);

      const wage = parseFloat(unionStraightTimeWage);
      if (!isNaN(wage)) {
        setValue("unionOverTimeWage", (wage * 1.5).toFixed(2));
      }
    }
  }, [isUnionOTWageFocused, unionOverTimeWageValue, unionStraightTimeWage, setValue]);

  useEffect(() => {
    if (!isUnionDTWageFocused && !unionDoubleTimeWageValue) {
      setIsUnionDTWageOverridden(false);

      const wage = parseFloat(unionStraightTimeWage);
      if (!isNaN(wage)) {
        setValue("unionDoubleTimeWage", (wage * 2).toFixed(2));
      }
    }
  }, [isUnionDTWageFocused, unionDoubleTimeWageValue, unionStraightTimeWage, setValue]);

  const handleJobTitleBlur = e => {
    const typedValue = e?.target?.value?.trim() ?? "";
    if (!typedValue) return;

    const lowerTyped = typedValue.toLowerCase();

    const isMatched = jobTitleOptions.some(opt => opt.label?.toLowerCase() === lowerTyped || String(opt.value)?.toLowerCase() === lowerTyped);

    const currentValue = getValues("jobTitleId");
    const isSelectedOption = jobTitleOptions.some(opt => String(opt.value) === String(currentValue));

    if (!isMatched && !isSelectedOption) {
      setInvalidJobTitleModal({
        open: true,
        message: `"${typedValue}" is not a valid Job Title. Please select from the available list.`
      });
      return;
    }
  };

  return (
    <>
      <ProToggleField
        {...defaultFormProps}
        {...formConfig.isExternalJobTitle}
        permission={addJobPermisison.fieldPerObj[formConfig.isExternalJobTitle.perKey]}
      />
      {isExternalJobTitle ? (
        <ProTextInput
          {...defaultFormProps}
          {...formConfig.externalJobTitle}
          permission={addJobPermisison.fieldPerObj[formConfig.externalJobTitle.perKey]}
        />
      ) : (
        <>
          <InfiniteScrollSelect
            {...defaultFormProps}
            {...formConfig.jobTitleId}
            permission={addJobPermisison.fieldPerObj[formConfig.jobTitleId.perKey]}
            options={jobTitleOptions}
            loading={jobTitleLoading}
            hasMore={jobTitleHasMore}
            loadMore={fetchJobTitles}
            currentPage={jobTitlePage}
            onSearch={fetchJobTitles}
            allowManualEntry
            onBlur={handleJobTitleBlur}
          />

          <DialogModal
            show={invalidJobTitleModal.open}
            title="Invalid Job Title"
            message={invalidJobTitleModal.message}
            onConfirm={() => {
              setValue("jobTitleId", "");
              setInvalidJobTitleModal({ open: false, message: "" });
            }}
            showCancel={false}
            confirmBtnText="OK"
          />
        </>
      )}
      <ProTextInput {...defaultFormProps} {...formConfig.jobInternalId} permission={addJobPermisison.fieldPerObj[formConfig.jobInternalId.perKey]} />
      {/* <InputWrapper size={4}></InputWrapper> */}
      <ProTextInput
        {...defaultFormProps}
        {...formConfig.jobDescription}
        onCustomBlur={getJobSearchOption}
        permission={addJobPermisison.fieldPerObj[formConfig.jobDescription.perKey]}
      />
      <FreeSoloAutoComplete
        {...defaultFormProps}
        {...formConfig.typeOfJob}
        iconInfoMessage={formConfig.typeOfJob.infoMessage}
        permission={addJobPermisison.fieldPerObj[formConfig.typeOfJob.perKey]}
        options={jobSearchOptions}
        withLoaderIcon={isFetchLabels}
        infoMessage={clrInfoMessage}
      />
      <ProTextInput
        {...defaultFormProps}
        {...formConfig.numberOfPositions}
        permission={addJobPermisison.fieldPerObj[formConfig.numberOfPositions.perKey]}
      />

      <ProSelectField
        {...defaultFormProps}
        {...formConfig.payTypePosition}
        options={payTypePositionOption}
        permission={addJobPermisison.fieldPerObj[formConfig.payTypePosition.perKey]}
      />

      <ProCurrencyField
        {...defaultFormProps}
        {...formConfig.perDiem}
        permission={addJobPermisison.fieldPerObj[formConfig.perDiem.perKey]}
        fieldProps={{
          onBlur: e => {
            if (e.target.value) setValue("perDiem", parseFloat(e.target.value).toFixed(2));
          }
        }}
      />
      {payTypePosition == "Salary" ? (
        <ProCurrencyField
          {...defaultFormProps}
          {...formConfig.salaryAmount}
          permission={addJobPermisison.fieldPerObj[formConfig.salaryAmount.perKey]}
          fieldProps={{
            onBlur: e => {
              if (e.target.value) {
                setValue("salaryAmount", parseFloat(e.target.value).toFixed(2));
              }
            }
          }}
        />
      ) : (
        <Grid md={4} xs={12} />
      )}
      <Grid container spacing={2} alignItems="center" sx={containerSx}>
        <Grid item xs={12} md={8}>
          <Box sx={cardSx}>
            <Typography fontWeight={400} mb={0.7}>
              Labor Classification Type *
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={2}>
              Select one classification that best describe this position
            </Typography>

            <ProRadioGroup
              size={12}
              {...defaultFormProps}
              {...formConfig.laborClassificationType}
              label=""
              options={laborOptions}
              permission={fieldTruePermission}
              sx={radioGroupSx}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <ProToggleField size={12} {...defaultFormProps} {...formConfig.isUnion} permission={fieldTruePermission} />
        </Grid>
      </Grid>

      {/* PSPD-1286: Temporarily hiding the Process button. */}
      {/* {isEditModeOn && (
        <Grid item xs={12} md={4}>
          <ProButton
            isSaving={isNoRecordOnClassification || isAdministrative || typeOfJob === null || typeOfJob?.length === 0}
            variant="contained"
            onClick={() => handleOpen()}
          >
            Process
          </ProButton>
        </Grid>
      )} */}

      <Grid item xs={12} md={12} mt={1} mb={2}>
        <Divider />
      </Grid>

      <FormSectionSubHeader title="Regular Time — Prevailing Wage" badge="Regular Hours" sx={{ marginTop: "0px", paddingTop: "0px" }} />

      <CustomCurrencyField
        {...defaultFormProps}
        {...formConfig.prevailingStraightTimeWage}
        permission={addJobPermisison.fieldPerObj[formConfig.prevailingStraightTimeWage.perKey]}
        fieldProps={{
          onBlur: e => {
            if (e.target.value) setValue("prevailingStraightTimeWage", parseFloat(e.target.value).toFixed(2));
          }
        }}
      />

      <CustomCurrencyField
        {...defaultFormProps}
        {...formConfig.prevailingStraightTimeFringe}
        permission={addJobPermisison.fieldPerObj[formConfig.prevailingStraightTimeFringe.perKey]}
        fieldProps={{
          onBlur: e => {
            if (e.target.value) setValue("prevailingStraightTimeFringe", parseFloat(e.target.value).toFixed(2));
          }
        }}
      />

      <CustomCurrencyField
        {...defaultFormProps}
        {...formConfig.prevailingStraightTimeTotalHourlyComp}
        fieldProps={{ disabled: true }}
        permission={addJobPermisison.fieldPerObj[formConfig.prevailingStraightTimeTotalHourlyComp.perKey]}
      />

      <FormSectionSubHeader title="OVERTIME — Prevailing Wage" badge="OVERTIME HOURS" />

      <CustomCurrencyField
        {...defaultFormProps}
        {...formConfig.prevailingOverTimeWage}
        permission={addJobPermisison.fieldPerObj[formConfig.prevailingOverTimeWage.perKey]}
        autoCalculatedIcon={overtimeWageIcon}
        showAutoCalculatedIcon={!isOTWageOverridden && !!Number(prevailingStraightTimeWage)}
        autoCalculatedTooltip="Auto-calculated from the Regular Time wage rate (1.5x)."
        fieldProps={{
            onChange: e => {
                if (e.target.value === "") return;

                const overtime = Number(e.target.value || 0);
                const straight = Number(getValues("prevailingStraightTimeWage") || 0);

                setIsOTWageOverridden(overtime !== Number((straight * 1.5).toFixed(2)));
            },
            onFocus: () => setIsOTWageFocused(true),
            onBlur: e => {
                setIsOTWageFocused(false);

                const currentValue = getValues("prevailingOverTimeWage");

                if (!currentValue) return;

                setValue("prevailingOverTimeWage", parseFloat(currentValue).toFixed(2));
            }
        }}
      />

      <CustomCurrencyField
        {...defaultFormProps}
        {...formConfig.prevailingOverTimeFringe}
        permission={addJobPermisison.fieldPerObj[formConfig.prevailingOverTimeFringe.perKey]}
        fieldProps={{
          onFocus: () => setIsOTFringeFocused(true),
          onBlur: e => {
            setIsOTFringeFocused(false);

            const currentValue = getValues("prevailingOverTimeFringe");

            if (!currentValue) return;

            const value = parseFloat(currentValue).toFixed(2);

            setValue("prevailingOverTimeFringe", value);

            setIsOTFringeOverridden(
              value !== parseFloat(prevailingStraightTimeFringe || 0).toFixed(2)
            );
          }
        }}
      />

      <CustomCurrencyField
        {...defaultFormProps}
        {...formConfig.prevailingOverTimeTotalHourlyComp}
        fieldProps={{ disabled: true }}
        permission={addJobPermisison.fieldPerObj[formConfig.prevailingOverTimeTotalHourlyComp.perKey]}
      />

      <FormSectionSubHeader title="DOUBLE TIME — Prevailing Wage" badge="DOUBLE TIME HOURS" />

      <CustomCurrencyField
        {...defaultFormProps}
        {...formConfig.prevailingDoubleTimeWage}
        permission={addJobPermisison.fieldPerObj[formConfig.prevailingDoubleTimeWage.perKey]}
        autoCalculatedIcon={doubleTimeWageIcon}
        showAutoCalculatedIcon={!isDTWageOverridden && !!Number(prevailingStraightTimeWage)}
        autoCalculatedTooltip="Auto-calculated from the Regular Time wage rate (2.0x)."
        fieldProps={{
            onChange: e => {
                if (e.target.value === "") return;

                const doubletime = Number(e.target.value || 0);
                const straight = Number(getValues("prevailingStraightTimeWage") || 0);

                setIsDTWageOverridden(
                    doubletime !== Number((straight * 2).toFixed(2))
                );
            },
            onFocus: () => setIsDTWageFocused(true),
            onBlur: e => {
                setIsDTWageFocused(false);

                const currentValue = getValues("prevailingDoubleTimeWage");

                if (!currentValue) return;

                setValue("prevailingDoubleTimeWage", parseFloat(currentValue).toFixed(2));
            }
        }}
      />

      <CustomCurrencyField
        {...defaultFormProps}
        {...formConfig.prevailingDoubleTimeFringe}
        permission={addJobPermisison.fieldPerObj[formConfig.prevailingDoubleTimeFringe.perKey]}
        fieldProps={{
          onFocus: () => setIsDTFringeFocused(true),
          onBlur: e => {
            setIsDTFringeFocused(false);

            const currentValue = getValues("prevailingDoubleTimeFringe");

            if (!currentValue) return;

            const value = parseFloat(currentValue).toFixed(2);

            setValue("prevailingDoubleTimeFringe", value);

            setIsDTFringeOverridden(
              value !== parseFloat(prevailingStraightTimeFringe || 0).toFixed(2)
            );
          }
        }}
      />

      <CustomCurrencyField
        {...defaultFormProps}
        {...formConfig.prevailingDoubleTimeTotalHourlyComp}
        fieldProps={{ disabled: true }}
        permission={addJobPermisison.fieldPerObj[formConfig.prevailingDoubleTimeTotalHourlyComp.perKey]}
      />

      {(groupClassification || equipmentUsed) && (
        <>
          <Grid item xs={12} mt={1} mb={2}>
            <Divider />
          </Grid>
          <ProTextInput
            {...defaultFormProps}
            {...formConfig.groupClassification}
            fieldProps={{ disabled: true }}
            permission={addJobPermisison.fieldPerObj[formConfig.prevailingStraightTimeTotalHourlyComp.perKey]}
          />
          <ProTextInput
            {...defaultFormProps}
            {...formConfig.equipmentUsed}
            permission={addJobPermisison.fieldPerObj[formConfig.prevailingStraightTimeTotalHourlyComp.perKey]}
          />
        </>
      )}
      {isUnionLaborFeatureEnabled && isUnion && (
        <>
          <Grid item xs={12} mt={1} mb={2}>
            <Divider />
          </Grid>
          <ProCurrencyField
            {...defaultFormProps}
            {...formConfig.unionStraightTimeWage}
            fieldProps={{ disabled: !isUnion }}
            permission={addJobPermisison.fieldPerObj[formConfig.unionStraightTimeWage.perKey]}
          />
          <ProCurrencyField
            {...defaultFormProps}
            {...formConfig.unionStraightTimeFringe}
            fieldProps={{ disabled: true }}
            permission={addJobPermisison.fieldPerObj[formConfig.unionStraightTimeFringe.perKey]}
          />
          <DateField
            {...defaultFormProps}
            {...formConfig.unionEffectiveDateEnd}
            permission={addJobPermisison.fieldPerObj[formConfig.unionEffectiveDateEnd.perKey]}
            warningLabel="Union Wage Rate"
            fieldProps={{
              disabled: !isUnion,
              onChange: value => {
                setValue("unionEffectiveDateEnd", value);
              }
            }}
            shouldDisableDate={getDisabledDate}
          />
          <ProCurrencyField
            {...defaultFormProps}
            {...formConfig.unionStraightTimeTotalHourlyComp}
            fieldProps={{ disabled: true }}
            permission={addJobPermisison.fieldPerObj[formConfig.unionStraightTimeTotalHourlyComp.perKey]}
          />

          <Grid item xs={12} mt={1} mb={2}>
            <ProjectJobsUnionFringeForm
              formConfig={formConfig}
              defaultFormProps={defaultFormProps}
              watch={watch}
              addJobPermisison={addJobPermisison}
            />
          </Grid>

          {isUnionOvertimeDoubletimeEnabled && (
            <>
              <Grid item xs={12} mt={2} mb={1}>
                <Typography sx={{ color: "#1976d2", fontWeight: 600, fontSize: 13, letterSpacing: 0.5 }}>OVERTIME — UNION</Typography>
              </Grid>
              <ProCurrencyField
                {...defaultFormProps}
                {...formConfig.unionOverTimeWage}
                rules={otDtRequiredRules(formConfig.unionOverTimeWage.rules, "Overtime Wage Rate", { requirePositive: true })}
                autoCalculatedIcon={overtimeWageIcon}
                showAutoCalculatedIcon={!isUnionOTWageOverridden && !!Number(unionStraightTimeWage)}
                autoCalculatedTooltip="Auto-calculated from the Union wage rate (1.5x)."
                fieldProps={{
                    disabled: !isUnion,
                    onChange: e => {
                        if (e.target.value === "") return;

                        const overtime = Number(e.target.value || 0);
                        const straight = Number(getValues("unionStraightTimeWage") || 0);

                        setIsUnionOTWageOverridden(
                            overtime !== Number((straight * 1.5).toFixed(2))
                        );
                    },
                    onFocus: () => setIsUnionOTWageFocused(true),
                    onBlur: e => {
                        setIsUnionOTWageFocused(false);

                        const currentValue = getValues("unionOverTimeWage");

                        if (!currentValue) return;

                        setValue("unionOverTimeWage", parseFloat(currentValue).toFixed(2));
                    }
                }}
                permission={addJobPermisison.fieldPerObj[formConfig.unionOverTimeWage.perKey]}
              />
              <ProCurrencyField
                {...defaultFormProps}
                {...formConfig.unionOverTimeFringe}
                rules={otDtRequiredRules(formConfig.unionOverTimeFringe.rules, "Overtime Fringe")}
                fieldProps={{ disabled: true }}
                permission={addJobPermisison.fieldPerObj[formConfig.unionOverTimeFringe.perKey]}
              />
              <ProCurrencyField
                {...defaultFormProps}
                {...formConfig.unionOverTimeTotalHourlyComp}
                rules={otDtRequiredRules(formConfig.unionOverTimeTotalHourlyComp.rules, "Overtime Total Hourly Comp")}
                fieldProps={{ disabled: true }}
                permission={addJobPermisison.fieldPerObj[formConfig.unionOverTimeTotalHourlyComp.perKey]}
              />
              <Grid item xs={12} mt={1}>
                <Box
                  sx={{
                    border: "1px solid #E0E0E0",
                    borderRadius: "8px",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#2e7d32" }} />
                    <Typography variant="body2" sx={{ color: "#161D2C" }}>
                      {formConfig.hasOTFringeOverride.label}
                    </Typography>
                    <Tooltip title="When enabled, you can override individual fringe categories for overtime.">
                      <IconButton size="small">
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <ProToggleField
                    size={1}
                    {...defaultFormProps}
                    {...formConfig.hasOTFringeOverride}
                    label=""
                    permission={addJobPermisison.fieldPerObj[formConfig.hasOTFringeOverride.perKey]}
                    fieldProps={{ disabled: !isUnion }}
                  />
                </Box>
              </Grid>
              {hasOTFringeOverride && (
                <Grid item xs={12} mt={1}>
                  <ProjectJobsOvertimeUnionFringeForm
                    formConfig={formConfig}
                    defaultFormProps={defaultFormProps}
                    watch={watch}
                    setValue={setValue}
                    addJobPermisison={addJobPermisison}
                    prefix="unionOverTime"
                    title="OVERTIME FRINGE OVERRIDES"
                    disabled={formConfig.unionOverTimeWage?.mode !== "edit"}
                    isInvalid={overrideErrors.ot}
                    onActivate={() => defaultFormProps.handleReadValueClick?.("unionOverTimeWage")}
                  />
                </Grid>
              )}

              <Grid item xs={12} mt={2} mb={1}>
                <Typography sx={{ color: "#1976d2", fontWeight: 600, fontSize: 13, letterSpacing: 0.5 }}>DOUBLE TIME — UNION</Typography>
              </Grid>
              <ProCurrencyField
                {...defaultFormProps}
                {...formConfig.unionDoubleTimeWage}
                rules={otDtRequiredRules(formConfig.unionDoubleTimeWage.rules, "Double-time Wage Rate", { requirePositive: true })}
                autoCalculatedIcon={doubleTimeWageIcon}
                showAutoCalculatedIcon={!isUnionDTWageOverridden && !!Number(unionStraightTimeWage)}
                autoCalculatedTooltip="Auto-calculated from the Union wage rate (2.0x)."
                fieldProps={{
                    disabled: !isUnion,
                    onChange: e => {
                        if (e.target.value === "") return;

                        const doubletime = Number(e.target.value || 0);
                        const straight = Number(getValues("unionStraightTimeWage") || 0);

                        setIsUnionDTWageOverridden(
                            doubletime !== Number((straight * 2).toFixed(2))
                        );
                    },
                    onFocus: () => setIsUnionDTWageFocused(true),
                    onBlur: e => {
                        setIsUnionDTWageFocused(false);

                        const currentValue = getValues("unionDoubleTimeWage");

                        if (!currentValue) return;

                        setValue("unionDoubleTimeWage", parseFloat(currentValue).toFixed(2));
                    }
                }}
                permission={addJobPermisison.fieldPerObj[formConfig.unionDoubleTimeWage.perKey]}
              />
              <ProCurrencyField
                {...defaultFormProps}
                {...formConfig.unionDoubleTimeFringe}
                rules={otDtRequiredRules(formConfig.unionDoubleTimeFringe.rules, "Double-time Fringe")}
                fieldProps={{ disabled: true }}
                permission={addJobPermisison.fieldPerObj[formConfig.unionDoubleTimeFringe.perKey]}
              />
              <ProCurrencyField
                {...defaultFormProps}
                {...formConfig.unionDoubleTimeTotalHourlyComp}
                rules={otDtRequiredRules(formConfig.unionDoubleTimeTotalHourlyComp.rules, "Double-time Total Hourly Comp")}
                fieldProps={{ disabled: true }}
                permission={addJobPermisison.fieldPerObj[formConfig.unionDoubleTimeTotalHourlyComp.perKey]}
              />
              <Grid item xs={12} mt={1}>
                <Box
                  sx={{
                    border: "1px solid #E0E0E0",
                    borderRadius: "8px",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#2e7d32" }} />
                    <Typography variant="body2" sx={{ color: "#161D2C" }}>
                      {formConfig.hasDTFringeOverride.label}
                    </Typography>
                    <Tooltip title="When enabled, you can override individual fringe categories for double-time.">
                      <IconButton size="small">
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <ProToggleField
                    size={1}
                    {...defaultFormProps}
                    {...formConfig.hasDTFringeOverride}
                    label=""
                    permission={addJobPermisison.fieldPerObj[formConfig.hasDTFringeOverride.perKey]}
                    fieldProps={{ disabled: !isUnion }}
                  />
                </Box>
              </Grid>
              {hasDTFringeOverride && (
                <Grid item xs={12} mt={1}>
                  <ProjectJobsOvertimeUnionFringeForm
                    formConfig={formConfig}
                    defaultFormProps={defaultFormProps}
                    watch={watch}
                    setValue={setValue}
                    addJobPermisison={addJobPermisison}
                    prefix="unionDoubleTime"
                    title="DOUBLE-TIME FRINGE OVERRIDES"
                    disabled={formConfig.unionDoubleTimeWage?.mode !== "edit"}
                    isInvalid={overrideErrors.dt}
                    onActivate={() => defaultFormProps.handleReadValueClick?.("unionDoubleTimeWage")}
                  />
                </Grid>
              )}
            </>
          )}
        </>
      )}

      <Grid item xs={12} md={12} mt={1} mb={2}>
        <Divider />
      </Grid>

      {/* SAM-match metadata (read-only). The legacy PW baseline wage/fringe rows were removed when the
          baseline collapsed into the single prevailing wage; the AI score + decision number remain. */}
      {isCraftTrade && SAMFilename && (
        <>
          <ProTextInput
            {...defaultFormProps}
            {...formConfig.pwAiConfidenceMatchScore}
            fieldProps={{ disabled: true }}
            permission={addJobPermisison.fieldPerObj[formConfig.pwAiConfidenceMatchScore.perKey]}
          />
          <ProTextInput
            {...defaultFormProps}
            {...formConfig.generalDecisionNumber}
            fieldProps={{ disabled: true }}
            permission={addJobPermisison.fieldPerObj[formConfig.prevailingStraightTimeTotalHourlyComp.perKey]}
          />
        </>
      )}

      {!!SAMFilename && (
        <Grid item xs={12} md={12}>
          <Link
            style={{ cursor: "pointer" }}
            onClick={() => {
              getPWCFilePath(SAMFilenamePath);
            }}
          >
            {SAMFilename}
          </Link>
        </Grid>
      )}

      {(footnoteSickDays || footnoteVacationDays || footnoteHolidays || footnoteOthers) && (
        <Grid item xs={12} md={12} mt={2}>
          <Typography variant="h7" fontWeight="400">
            Footnotes
          </Typography>
          <Divider />
        </Grid>
      )}
      {footnoteSickDays && (
        <ProTextInput
          {...defaultFormProps}
          {...formConfig.footnoteSickDays}
          permission={addJobPermisison.fieldPerObj[formConfig.prevailingStraightTimeTotalHourlyComp.perKey]}
        />
      )}
      {footnoteVacationDays && (
        <ProTextInput
          {...defaultFormProps}
          {...formConfig.footnoteVacationDays}
          permission={addJobPermisison.fieldPerObj[formConfig.prevailingStraightTimeTotalHourlyComp.perKey]}
        />
      )}
      {footnoteHolidays && (
        <ProTextInput
          {...defaultFormProps}
          {...formConfig.footnoteHolidays}
          permission={addJobPermisison.fieldPerObj[formConfig.prevailingStraightTimeTotalHourlyComp.perKey]}
        />
      )}
      {footnoteOthers && (
        <ProTextInput
          {...defaultFormProps}
          {...formConfig.footnoteOthers}
          permission={addJobPermisison.fieldPerObj[formConfig.prevailingStraightTimeTotalHourlyComp.perKey]}
        />
      )}

      {/* <Grid item xs={12} md={12} mt={1} mb={2}>
        <Divider />
      </Grid> */}

      <Grid item xs={12} md={12}>
        <Box Box display="flex" alignItems="center" gap={4}>
          <ProCheckbox
            {...defaultFormProps}
            {...formConfig.isApprenticeable}
            disabled={false}
            permission={addJobPermisison.fieldPerObj[formConfig.isApprenticeable.perKey]}
          />

          <ProCheckbox {...defaultFormProps} {...formConfig.isCustomApprenticeOnboardingRequired} disabled={false} permission={fieldTruePermission} />
        </Box>
      </Grid>

      {isApprenticeable && (
        <>
          <Grid item xs={12} md={12}>
            <Typography className="project-job-text" variant="h6" fontWeight="400">
              {getApprenticeTitle()}
            </Typography>
          </Grid>

          <ProSelectField
            {...defaultFormProps}
            {...localFormConfig.assignRAPInfoGuid}
            permission={addJobPermisison.fieldPerObj[localFormConfig.assignRAPInfoGuid.perKey]}
            options={assignRAPInfoGuids}
            required={!!localFormConfig.assignRAPInfoGuid.rules}
          />
          <ProSelectField
            {...defaultFormProps}
            {...formConfig.assignRAPWageScaleGuid}
            options={assignRAPWageScaleGuids}
            permission={addJobPermisison.fieldPerObj[formConfig.assignRAPWageScaleGuid.perKey]}
          />
          <ProTextInput
            {...defaultFormProps}
            {...formConfig.occupationTitle}
            fieldProps={{ disabled: true }}
            permission={addJobPermisison.fieldPerObj[formConfig.occupationTitle.perKey]}
          />
          <ProTextInput
            {...defaultFormProps}
            {...formConfig.onetCode}
            fieldProps={{ disabled: true }}
            permission={addJobPermisison.fieldPerObj[formConfig.onetCode.perKey]}
          />
        </>
      )}
      {isApprenticeable && isCustomApprenticeOnboardingRequired && (
        <Grid item xs={12} md={12}>
          <ApprenticeTable
            isEditModeOn={isEditModeOn}
            defaultFormProps={defaultFormProps}
            rows={apprenticeRows}
            setRows={setApprenticeRows}
            additionalValue={additionalApprenticeValue}
            setAdditionalValue={setAdditionalApprenticeValue}
          />
        </Grid>
      )}
    </>
  );
};

export default ProjectJobsForm;

ProjectJobsForm.propTypes = {
  isApprenticeable: PropTypes.bool.isRequired,
  isCustomApprenticeOnboardingRequired: PropTypes.bool.isRequired,
  getApprenticeTitle: PropTypes.func.isRequired,
  handleOpen: PropTypes.func.isRequired,
  defaultFormProps: PropTypes.object.isRequired,
  formConfig: PropTypes.shape({
    assignRAPInfoGuid: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    assignRAPWageScaleGuid: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    generalDecisionNumber: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    occupationTitle: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    onetCode: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    isExternalJobTitle: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    externalJobTitle: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    jobTitleId: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    numberOfPositions: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    jobDescription: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    payTypePosition: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    perDiem: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    IsCraftTradeLaborPosition: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    IsRole: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    salaryAmount: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    marketRate: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    pwAiConfidenceMatchScore: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,

    typeOfJob: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    isApprenticeable: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    footnoteSickDays: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    footnoteVacationDays: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    footnoteOthers: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired,
    footnoteHolidays: PropTypes.shape({
      perKey: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  addJobPermisison: PropTypes.shape({
    fieldPerObj: PropTypes.objectOf(
      PropTypes.shape({
        perKey: PropTypes.string.isRequired
      })
    ).isRequired
  }).isRequired,
  assignRAPInfoGuids: PropTypes.array.isRequired,
  assignRAPWageScaleGuids: PropTypes.array.isRequired,
  isExternalJobTitle: PropTypes.bool.isRequired,
  jobTitle: PropTypes.array.isRequired,
  payTypePosition: PropTypes.string.isRequired,
  payTypePositionOption: PropTypes.array.isRequired,
  isCraftTrade: PropTypes.bool.isRequired,
  chatBotRates: PropTypes.any,
  wsPayload: PropTypes.shape({
    state: PropTypes.any,
    jobLongitude: PropTypes.any,
    county: PropTypes.any,
    city: PropTypes.any,
    jobLatitude: PropTypes.any
  }).isRequired,
  watch: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  jobSearchOptions: PropTypes.array.isRequired,
  overrideErrors: PropTypes.shape({
    ot: PropTypes.bool,
    dt: PropTypes.bool
  })
};